import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SocketProvider } from './contexts/SocketContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CharacterCreate from './pages/CharacterCreate';
import Game from './pages/Game';
import AuthLanding from './pages/AuthLanding';
import EarlyAccess from './pages/EarlyAccess';
import LoadingSpinner from './components/LoadingSpinner';

function PrivateRoute({ children }) {
  const { isAuthenticated, hasEarlyAccess, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }

  if (!hasEarlyAccess) {
    return <Navigate to="/early-access" />;
  }

  return children;
}

function EarlyAccessRoute({ children }) {
  const { isAuthenticated, hasEarlyAccess, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }

  if (hasEarlyAccess) {
    return <Navigate to="/play" />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading..." />;
  }

  return isAuthenticated ? <Navigate to="/play" /> : children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Redirect root to appropriate page */}
      <Route path="/" element={<Navigate to="/play" />} />

      {/* Public auth routes */}
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <AuthLanding />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Early access gate */}
      <Route
        path="/early-access"
        element={
          <EarlyAccessRoute>
            <EarlyAccess />
          </EarlyAccessRoute>
        }
      />

      {/* Legacy redirects */}
      <Route path="/dashboard" element={<Navigate to="/play" />} />

      {/* Private routes (require early access) */}
      <Route
        path="/play"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/play/create-character"
        element={
          <PrivateRoute>
            <CharacterCreate />
          </PrivateRoute>
        }
      />
      <Route
        path="/play/adventure/:gameId"
        element={
          <PrivateRoute>
            <Game />
          </PrivateRoute>
        }
      />

      {/* Legacy game route redirect */}
      <Route path="/game/:gameId" element={<LegacyGameRedirect />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/play" />} />
    </Routes>
  );
}

// Redirect old /game/:id to new /play/adventure/:id
function LegacyGameRedirect() {
  const gameId = window.location.pathname.split('/game/')[1];
  return <Navigate to={`/play/adventure/${gameId}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <AppRoutes />
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
